import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Text,
  Input,
  Button,
  Form,
  FlexGrid,
  LoadingMask,
} from '@gasbuddy/react-components';
import classnames from 'classnames/bind';
import querystring from 'querystring';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLocationArrow } from '@fortawesome/free-solid-svg-icons/faLocationArrow';
import { faSearch } from '@fortawesome/free-solid-svg-icons/faSearch';
import TrackerMap from '../TrackerMap';
import StationListItem from '../StationListItem';
import Legend from '../Legend';
import SplitScreen from '../SplitScreen';
import Navbar from '../Navbar';
import { version } from '../../../../package.json';
import styles from './MainPage.module.css';
import useStations from '../../../client/hooks/useStations';
import useLocation from '../../../client/hooks/useLocation';
import DefaultHelmet from './DefaultHelmet';

const cx = classnames.bind(styles);

const INITIAL_CENTER = { lat: 32.69783, lng: -80.91777 };

export default function MainPage({
  history,
  location,
}) {
  const queryParams = querystring.parse((location.search || '?').substring(1));
  const [mapDragged, setMapDragged] = useState(false);
  const [activeStation, setActiveStation] = useState(queryParams.station_id || null);
  const [query, setQuery] = useState(queryParams.q || '');
  const [trackerMap, setTrackerMap] = useState();
  let coordinates;

  // If the user supplied a lat/lng pair in the query string, those coordinates would become the result location
  if (queryParams.lat && queryParams.lng) {
    coordinates = {
      lat: parseFloat(queryParams.lat),
      lng: parseFloat(queryParams.lng),
    };
  }

  const [geocodedLocation, { isLoading: isGeocoding }] = useLocation(!coordinates ? queryParams.q : undefined);
  const [stations, { isLoading: isSearching }] = useStations(coordinates || geocodedLocation);
  const isLoading = isGeocoding || isSearching;

  const searchLocation = useMemo(() => {
    // If the user has focused on a station, return the station's coordinates
    if (queryParams.station_id) {
      const station = stations.find(s => s.id === queryParams.station_id);
      if (station) {
        return { lat: station.latitude, lng: station.longitude };
      }
    }

    return coordinates || geocodedLocation || INITIAL_CENTER;
  }, [geocodedLocation, queryParams.station_id, coordinates]);

  const handleMapLoad = useCallback((map) => {
    setTrackerMap(map);
  }, []);

  const searchMapLocation = useCallback(() => {
    const mapCenter = trackerMap.getCenter();
    const lat = mapCenter.lat();
    const lng = mapCenter.lng();

    queryParams.lat = lat;
    queryParams.lng = lng;
    delete queryParams.q;

    history.push({
      search: `?${querystring.stringify(queryParams)}`,
    });

    setActiveStation(null);
    setMapDragged(false);
  }, [history, queryParams, trackerMap]);

  // Attempt to use HTML5 Geolocation
  const geolocate = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.navigator.geolocation.getCurrentPosition((position) => {
        const { coords: userLocation } = position;

        if (queryParams.lat !== userLocation.latitude && queryParams.lng !== userLocation.longitude) {
          queryParams.lat = userLocation.latitude;
          queryParams.lng = userLocation.longitude;

          delete queryParams.q;

          history.push({
            search: `?${querystring.stringify(queryParams)}`,
          });

          setMapDragged(false);
        }
      }, () => {
        // TODO Handle error here
      });
    }
  }, [history, queryParams]);

  const handleMarkerShow = useCallback(({ key }) => {
    setActiveStation(key);
  }, []);

  const handleMarkerHide = useCallback(({ key }) => {
    if (key === activeStation) {
      setActiveStation(null);
    }
  }, [activeStation]);

  const stationItemClickHandlers = useMemo(() => (
    stations.map(station => () => { setActiveStation(station.id); })
  ), [stations]);

  const handleMapDragged = useCallback(() => {
    setMapDragged(true);
  }, []);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();

    queryParams.q = query;
    delete queryParams.station_id;
    delete queryParams.lat;
    delete queryParams.lng;

    history.push({
      search: `?${querystring.stringify(queryParams)}`,
    });
  }, [history, query, queryParams]);

  const handleQueryChange = useCallback((e) => {
    setQuery(e.target.value);
  }, []);

  // Update query parameters if local state has changed
  useEffect(() => {
    const needsUpdate = activeStation && queryParams.station_id !== activeStation.toString();
    const needsRemoval = !activeStation && queryParams.station_id;

    if (needsUpdate) {
      queryParams.station_id = activeStation;
    } else if (needsRemoval) {
      delete queryParams.station_id;
    }

    if (needsUpdate || needsRemoval) {
      history.push({
        search: `?${querystring.stringify(queryParams)}`,
      });
    }
  }, [activeStation, history, queryParams]);

  // Update local state if query string changes
  useEffect(() => {
    setQuery(queryParams.q || '');
  }, [queryParams.q]);

  // Update local state if query string changes
  useEffect(() => {
    setActiveStation(queryParams.station_id ? parseInt(queryParams.station_id, 10) : null);
  }, [queryParams.station_id]);

  // Generate markers for map
  const markers = useMemo(() => (
    stations.map(station => ({
      key: station.id,
      showInfo: station.id === activeStation,
      station,
    }))
  ), [activeStation, stations]);

  const supportsGeolocation = typeof window !== 'undefined' && 'geolocation' in window.navigator;

  return (
    <div>
      <DefaultHelmet />
      <Navbar>
        {mapDragged
          && (
            <Button onClick={searchMapLocation} primary className={cx('floatRight')}>
              <span className={cx('nonMobile')}>Redo Search in This Area</span>
              <span className={cx('mobile')}>Search Here</span>
            </Button>
          )
        }
      </Navbar>
      <SplitScreen style={{ top: 60 }}>
        <SplitScreen.StaticPane>
          <TrackerMap
            onMapLoad={handleMapLoad}
            center={mapDragged ? undefined : searchLocation}
            markers={markers}
            onMarkerShow={handleMarkerShow}
            onMarkerHide={handleMarkerHide}
            onDragEnd={handleMapDragged}
          />
        </SplitScreen.StaticPane>
        <SplitScreen.SlidingPane>
          <Form method="POST" action="/" onSubmit={handleSubmit}>
            <Input
              innerComponent={
                supportsGeolocation
                  ? <FontAwesomeIcon icon={faLocationArrow} className={cx('locationIcon')} onClick={geolocate} />
                  : <FontAwesomeIcon icon={faSearch} className={cx('searchIcon')} />
              }
              label="Search for stations"
              onChange={handleQueryChange}
              value={query}
              disabled={isLoading}
            />
          </Form>
          <Legend />
          <LoadingMask isLoading={isLoading} message="Loading..." className={cx('loadingMask')} />
          <div className={cx('results')}>
            <FlexGrid>
              {stations.map((station, index) => (
                <FlexGrid.Column desktop={6} key={station.id}>
                  <StationListItem
                    id={station.id}
                    address={station.address}
                    name={station.name}
                    brandName={station.brand_name}
                    distance={station.distance}
                    icon={(station.brands[0] || {}).image_url}
                    phone={station.phone}
                    onClick={stationItemClickHandlers[index]}
                    fuels={station.fuels}
                    emergencyStatus={station.emergency_status}
                  />
                </FlexGrid.Column>
              ))}
            </FlexGrid>
            <div className={cx(stations.length ? 'versionAndCopyrightInfo' : 'fixedVersionAndCopyrightInfo')}>
              <Text as="small">
                v {version} &copy; {new Date().getFullYear()} GasBuddy LLC
              </Text>
            </div>
          </div>
        </SplitScreen.SlidingPane>
      </SplitScreen>
    </div>
  );
}

MainPage.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  location: PropTypes.shape({
    search: PropTypes.string,
  }).isRequired,
};
